import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import Wordmark from '../components/Wordmark';
import DemoWalkthrough from '../components/DemoWalkthrough';

/**
 * MarketingPage — public-facing static-content surface (the live render
 * of the markdown drafts in docs/marketing/, served from /public/marketing/
 * at /about, /help, /privacy, /terms, /landing).
 *
 * Walter 2026-05-09: published before public traffic exists so Walter can
 * see how the docs read in real listicity chrome (header / footer / type
 * scale / palette) before the lawyer review pass. Source of truth stays
 * the .md files in docs/marketing/; the build script (or a manual sync)
 * copies them into /public/marketing/ where this component fetches them.
 *
 * Design choices:
 *   • single column, max ~720px, generous line-height — long-read mode.
 *   • mono headers + sans body — matches the app's mono-warm aesthetic
 *     without aping the editor exactly.
 *   • simple top header with the wordmark linking back to /, and a
 *     footer that links between the marketing pages.
 *   • the "Status: draft for review" / "Resolved (date) /" bookkeeping
 *     blocks at the bottom of each .md are filtered out at render time
 *     so users don't see internal review notes.
 */
const PAGES = {
  landing: { file: 'home.md',    title: 'listicity'           },
  about:   { file: 'about.md',   title: 'about · listicity'   },
  help:    { file: 'help.md',    title: 'help · listicity'    },
  privacy: { file: 'privacy.md', title: 'privacy · listicity' },
  terms:   { file: 'terms.md',   title: 'terms · listicity'   },
};

// Strip internal review blocks (Status, Resolved, Still open, Open
// questions for Walter) from the markdown before rendering. They're
// useful in the source files but not for end-users.
function stripInternalBlocks(md) {
  if (!md) return '';
  // Drop everything from the first internal-block heading onwards. The
  // headings we use in the .md files are: "## Resolved (...)", "## Still
  // open", "## Open questions for Walter", "## Notes for future-me".
  // First match wins.
  const cutMarkers = [
    /^## Resolved\b/m,
    /^## Still open\b/m,
    /^## Open questions for Walter\b/m,
    /^## Notes for future-me\b/m,
  ];
  let earliest = md.length;
  for (const re of cutMarkers) {
    const m = md.match(re);
    if (m && m.index < earliest) earliest = m.index;
  }
  let out = md.slice(0, earliest).trimEnd();
  // Strip the small "Status: ..." / "Tone: ..." / "Last updated: ..."
  // header lines that sit just under the H1 in our drafts.
  out = out.replace(/^Status:.*\n?/gim, '');
  out = out.replace(/^Tone:.*\n?/gim, '');
  out = out.replace(/^Last updated:.*\n?/gim, '');
  return out.trim();
}

export default function MarketingPage({ slug, isAuthed, hasSession = false, signupsPaused = false }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  // Walter 2026-06-16: the demo walkthrough is reachable "under the help
  // page" — a launch button on /help opens the same self-contained player.
  const [showDemo, setShowDemo] = useState(false);
  const navigate = useNavigate();
  const meta = PAGES[slug] || PAGES.landing;

  useEffect(() => {
    document.title = meta.title;
    setContent(null);
    setError(null);
    // Walter 2026-05-18: dropped the `?v=${Date.now()}` cache-buster.
    // It made every fetch URL unique → the service worker's
    // stale-while-revalidate couldn't match a cached version → every
    // marketing page load was a full network round-trip in the PWA,
    // visible to Walter as a "strangely long lag." Without the
    // cache-buster the SW returns instantly from cache and refreshes
    // in the background; the next visit sees the updated copy.
    // Marketing copy doesn't change often, so the one-load-behind
    // freshness model is fine.
    fetch(`/marketing/${meta.file}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(text => setContent(stripInternalBlocks(text)))
      .catch(e => setError(e.message));
  }, [slug, meta.file, meta.title]);

  // Primary CTA on the landing page. /board is a "smart redirect"
  // surface: signed-in users hit the catch-all and bounce to their
  // first board; signed-out users hit the LoginPage fall-through in
  // App.js and see the sign-in widget. Walter 2026-05-13: was /
  // for signed-out, but / now renders the landing for them too —
  // which would loop the CTA back to the same page.
  // Walter 2026-06-06: when new sign-ups are paused, the signed-out CTA
  // becomes "Join the waitlist" and links to /board#waitlist so it lands
  // straight on the waitlist form (App.js reads the #waitlist hash).
  // Signed-in users always get the normal "Open your boards".
  // A guest (hasSession but not isAuthed) is never "paused out" — they're
  // already in. Only a brand-new visitor sees the waitlist CTA.
  const ctaPaused = signupsPaused && !isAuthed && !hasSession;
  const ctaHref = ctaPaused ? '/board#waitlist' : '/board';
  // Walter 2026-05-13: arrow renders as its own span (see below) so
  // it can be flex-centered against the text baseline. Label kept
  // arrow-less here.
  // Walter 2026-06-24: on the in-app doc pages (help/about/privacy/terms) a
  // signed-in user arrived FROM their board, so the CTA should take them
  // "back to board". The landing page keeps the inviting "Open your boards"
  // (it's the primary entry CTA, not a return trip).
  const ctaLabel = isAuthed
    ? (slug === 'landing' ? 'Open your boards' : 'back to board')
    : (hasSession
      ? 'back to your board'
      : (ctaPaused ? 'Join the waitlist' : 'try listicity (no account required)'));

  // Walter 2026-05-12: the landing page ships LIGHT regardless of the
  // user's selected theme. Walter induced a light variant by accident
  // and preferred it — light mode for the home page reads as more
  // welcoming + matches the trend among productivity-tool sites. Other
  // marketing pages (about/help/privacy/terms) still inherit whatever
  // theme the user picked in the app so the reading experience stays
  // consistent with their preference. The forced theme is scoped to
  // this wrapper via data-theme="light"; CSS vars cascade from here.
  const isLandingLight = slug === 'landing';
  return (
    <div
      {...(isLandingLight ? { 'data-theme': 'light' } : {})}
      style={{
        minHeight: '100dvh',
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: 'var(--sans)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — wordmark links home; "open the app" link on the
          right does the right thing depending on auth state.
          Walter 2026-05-13: shorter CTA label on narrow viewports so
          it doesn't crowd the wordmark — the full label is preserved
          on desktop. */}
      <header style={{
        height: 'var(--header-h)',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 14px',
        flexShrink: 0,
        // Walter 2026-06-06: keep the header (and its CTA) pinned as the
        // page scrolls — the landing is long and the only call-to-action
        // used to scroll away, leaving no way to start without scrolling
        // back up.
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <Link to="/landing" style={{ textDecoration: 'none' }}>
          <Wordmark fontSize={16} />
        </Link>
        <button
          className="lists-marketing-cta"
          onClick={() => navigate(ctaHref)}
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            padding: '7px 14px',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            // Walter 2026-05-13: inline-flex with align-items: center
            // so the trailing arrow vertically aligns with the text.
            // In monospace fonts the "→" glyph centers around the
            // cell rather than the baseline; without flex centering
            // it sat noticeably higher than the letters on mobile.
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            lineHeight: 1,
          }}
        >
          <span className="lists-cta-long">{ctaLabel}</span>
          <span className="lists-cta-short">{isAuthed ? 'open' : (hasSession ? 'back' : (ctaPaused ? 'waitlist' : 'try it'))}</span>
          {/* Walter 2026-05-13: arrow as SVG, not the "→" glyph.
              The mono font centers → above the letter baseline by
              ~2px even with align-items: center, so flexing alone
              didn't fix it. SVG renders at exact pixel coords. */}
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            aria-hidden="true"
            style={{ display: 'block', flex: 'none' }}
          >
            <path
              d="M2 6h7M6 3l3 3-3 3"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </header>

      {/* Body — single column, generous reading width. The landing
          page gets a wider article + extra .is-landing class so its
          hero h1, h2, h3 and feature-card layout get bumped to the
          home-page styling defined in the inline <style> block below. */}
      <main
        className={`lists-marketing-main${slug === 'landing' ? ' is-landing' : ''}`}
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <article
          className={`lists-marketing${slug === 'landing' ? ' is-landing' : ''}`}
          style={{
            width: '100%',
            maxWidth: slug === 'landing' ? 880 : 720,
            fontSize: '15px',
            lineHeight: 1.65,
          }}
        >
          {/* Walter 2026-06-16: walkthrough launcher on the help page. */}
          {slug === 'help' && (
            <button
              type="button"
              onClick={() => setShowDemo(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                marginBottom: 18,
                background: 'var(--accent)',
                color: 'var(--bg)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius)',
                padding: '9px 16px',
                fontFamily: 'var(--mono)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {/* Walter 2026-06-25: plain text + »» so it reads as a quick
                  slide tour (the dots looked like a loading spinner); ~10s. */}
              View the 10-second tour <span aria-hidden="true">»</span>
            </button>
          )}
          {error && (
            <div style={{
              padding: '20px',
              background: 'var(--surface2)',
              border: '1px dashed var(--danger)',
              borderRadius: 'var(--radius)',
              color: 'var(--text-dim)',
              fontFamily: 'var(--mono)',
              fontSize: '13px',
            }}>
              couldn't load this page ({error}). try refreshing, or{' '}
              <Link to="/landing" style={{ color: 'var(--accent)' }}>go home</Link>.
            </div>
          )}
          {content === null && !error && (
            <div style={{
              fontFamily: 'var(--mono)',
              color: 'var(--text-dimmer)',
              fontSize: '12px',
            }}>
              loading…
            </div>
          )}
          {content && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
            >
              {content}
            </ReactMarkdown>
          )}

          <DemoWalkthrough open={showDemo} onClose={() => setShowDemo(false)} />

          {/* Closing CTA — only on the landing page. Gives readers
              who scroll to the bottom a second click target without
              forcing them to scroll back to the header CTA. */}
          {slug === 'landing' && content && (
            <div className="lists-landing-closing-cta" style={{
              marginTop: '60px',
              padding: '36px 24px',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}>
              <span style={{
                fontFamily: 'var(--mono)',
                fontSize: '16px',
                color: 'var(--text)',
                fontWeight: 600,
              }}>
                Free, no account required.
              </span>
              <span style={{
                fontFamily: 'var(--sans)',
                fontSize: '14px',
                color: 'var(--text-dim)',
                maxWidth: '440px',
              }}>
                A quick look takes 2 clicks — try it as a guest and sign in later if you want to keep what you made.
              </span>
              <button
                onClick={() => navigate(ctaHref)}
                style={{
                  background: 'var(--accent)',
                  color: 'var(--bg)',
                  border: 'none',
                  padding: '12px 28px',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  letterSpacing: '0.3px',
                  marginTop: '4px',
                  // Same arrow-alignment fix as the header CTA above.
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  lineHeight: 1,
                }}
              >
                <span>{ctaLabel}</span>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                  style={{ display: 'block', flex: 'none' }}
                >
                  <path
                    d="M2 6h7M6 3l3 3-3 3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            </div>
          )}
        </article>
      </main>

      {/* Footer — links between marketing pages + back to the app. */}
      <footer style={{
        borderTop: '1px solid var(--border)',
        padding: '24px 20px',
        background: 'var(--surface)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '20px',
        fontFamily: 'var(--mono)',
        fontSize: '12px',
        color: 'var(--text-dim)',
      }}>
        <Link to="/landing" style={footerLinkStyle(slug === 'landing')}>home</Link>
        <Link to="/about"   style={footerLinkStyle(slug === 'about')}>about</Link>
        <Link to="/help"    style={footerLinkStyle(slug === 'help')}>help</Link>
        <Link to="/privacy" style={footerLinkStyle(slug === 'privacy')}>privacy</Link>
        <Link to="/terms"   style={footerLinkStyle(slug === 'terms')}>terms</Link>
        {/* Walter 2026-05-10: glyph + word, hairline gap so they read
            as a single contact affordance. No dot separator before
            this — the icon is enough to mark contact as distinct from
            the legal/info links to its left. */}
        <Link to="/contact" style={footerLinkStyle(false)}>
          <span style={{ marginRight: 4 }}>✉</span>contact
        </Link>
      </footer>

      {/* Inline page-scoped styles for the rendered markdown. Kept here
          rather than in index.css so they only apply to .lists-marketing
          and don't leak into the editor / sidebar / agenda. */}
      <style>{`
        /* Walter 2026-05-13: CTA label swaps between full ("Start
           using listicity →") and short ("start →") at the 480-px
           breakpoint so the header stays clean on phones. */
        .lists-cta-short { display: none; }
        @media (max-width: 480px) {
          .lists-cta-long { display: none; }
          .lists-cta-short { display: inline; }
        }
        /* Main padding — tighter on mobile so the landing isn't
           framed by 60px of empty space above the hero. */
        .lists-marketing-main { padding: 40px 20px 60px; }
        .lists-marketing-main.is-landing { padding: 60px 20px 80px; }
        @media (max-width: 600px) {
          .lists-marketing-main { padding: 24px 14px 40px; }
          .lists-marketing-main.is-landing { padding: 28px 14px 48px; }
        }
        .lists-marketing h1 {
          font-family: var(--mono);
          font-size: 28px;
          font-weight: 700;
          color: var(--text);
          margin: 0 0 24px;
          letter-spacing: -0.5px;
        }
        .lists-marketing h2 {
          font-family: var(--mono);
          font-size: 18px;
          font-weight: 600;
          color: var(--accent);
          margin: 36px 0 12px;
          letter-spacing: -0.3px;
        }
        .lists-marketing h3 {
          font-family: var(--mono);
          font-size: 15px;
          font-weight: 600;
          color: var(--text);
          margin: 24px 0 8px;
        }
        .lists-marketing p {
          margin: 0 0 14px;
          color: var(--text);
        }
        .lists-marketing strong { color: var(--text); font-weight: 600; }
        .lists-marketing em { color: var(--text-dim); font-style: italic; }
        .lists-marketing a {
          color: var(--accent);
          text-decoration: none;
          border-bottom: 1px solid var(--accent-dim);
        }
        .lists-marketing a:hover { color: var(--accent-dim); }
        .lists-marketing ul, .lists-marketing ol {
          margin: 0 0 16px 22px;
          padding: 0;
        }
        .lists-marketing li { margin: 0 0 6px; }
        .lists-marketing hr {
          border: none;
          border-top: 1px solid var(--border);
          margin: 28px 0;
        }
        .lists-marketing code {
          font-family: var(--mono);
          font-size: 13px;
          background: var(--surface2);
          border: 1px solid var(--border);
          border-radius: 3px;
          padding: 1px 5px;
          color: var(--accent);
        }
        .lists-marketing pre {
          background: var(--surface2);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 12px 14px;
          overflow-x: auto;
          margin: 0 0 16px;
        }
        .lists-marketing pre code {
          background: none;
          border: none;
          padding: 0;
          color: var(--text);
        }
        .lists-marketing table {
          border-collapse: collapse;
          margin: 0 0 18px;
          width: 100%;
          font-family: var(--mono);
          font-size: 13px;
        }
        .lists-marketing th, .lists-marketing td {
          border: 1px solid var(--border);
          padding: 7px 10px;
          text-align: left;
          vertical-align: top;
        }
        .lists-marketing th {
          background: var(--surface2);
          color: var(--accent);
          font-weight: 600;
        }
        .lists-marketing blockquote {
          margin: 0 0 16px;
          padding: 10px 14px;
          border-left: 3px solid var(--accent-dim);
          background: var(--surface2);
          color: var(--text-dim);
        }
        .lists-marketing blockquote p:last-child { margin-bottom: 0; }

        /* Landing-page overrides — bigger hero h1, centered intro,
           and a 3-column grid for the "Why listicity" feature cards.
           Walter 2026-05-09: the standard markdown render felt too
           doc-like for the home page. The .is-landing scope keeps
           every other marketing page (about/help/privacy/terms)
           rendering at the original reading-doc dimensions. */
        .lists-marketing.is-landing h1 {
          font-size: 44px;
          line-height: 1.1;
          text-align: center;
          margin: 0 auto 20px;
          max-width: 720px;
          letter-spacing: -1px;
        }
        @media (max-width: 600px) {
          .lists-marketing.is-landing h1 { font-size: 32px; letter-spacing: -0.5px; }
        }
        .lists-marketing.is-landing > p:first-of-type,
        .lists-marketing.is-landing h1 + p {
          text-align: center;
          font-size: 18px;
          line-height: 1.55;
          color: var(--text-dim);
          max-width: 640px;
          margin: 0 auto 16px;
        }
        @media (max-width: 600px) {
          .lists-marketing.is-landing > p:first-of-type,
          .lists-marketing.is-landing h1 + p { font-size: 16px; }
        }
        /* Hero imagery. Walter 2026-05-12: the landing now ships TWO
           images — a wide desktop canvas mock and a narrow mobile mock.
           Both share the same elevation treatment but the mobile one
           sits inline (next to the desktop on wide viewports) and
           caps its height so it doesn't dominate. Max width matches
           the article container (880px) so the hero strip lines up
           with the feature cards below. */
        .lists-marketing.is-landing p:has(> img) {
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          align-items: stretch;
          justify-content: center;
          margin: 28px auto 4px;
          max-width: 880px;
        }
        .lists-marketing.is-landing img {
          display: block;
          height: auto;
          border-radius: 8px;
          border: 1px solid var(--border-bright);
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(232, 98, 44, 0.06);
        }
        /* Desktop canvas — takes the wide slot. */
        .lists-marketing.is-landing img[src*="canvas-hero"] {
          flex: 1 1 560px;
          min-width: 280px;
          max-width: 640px;
        }
        /* Mobile mock — narrower, capped. */
        .lists-marketing.is-landing img[src*="mobile-hero"] {
          flex: 0 1 220px;
          min-width: 180px;
          max-width: 240px;
        }
        @media (max-width: 600px) {
          .lists-marketing.is-landing p:has(> img) {
            gap: 12px;
            margin: 18px auto 4px;
          }
          .lists-marketing.is-landing img {
            border-radius: 6px;
            /* Walter 2026-05-13: lighter shadow on mobile — the
               18/44/0.32 desktop drop makes the hero look like it's
               floating way above the canvas at small sizes. Just
               enough lift to read as elevated, not levitating. */
            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(232, 98, 44, 0.04);
          }
          .lists-marketing.is-landing img[src*="canvas-hero"] {
            flex: 1 1 100%;
            max-width: 100%;
          }
          .lists-marketing.is-landing img[src*="mobile-hero"] {
            flex: 0 1 140px;
          }
        }
        .lists-marketing.is-landing h2 {
          font-size: 22px;
          text-align: center;
          margin: 56px 0 28px;
          letter-spacing: -0.4px;
        }
        @media (max-width: 600px) {
          .lists-marketing.is-landing h2 {
            font-size: 19px;
            margin: 36px 0 20px;
          }
        }
        .lists-marketing.is-landing h3 {
          font-size: 16px;
          color: var(--accent);
          margin: 0 0 10px;
        }
        /* Three-card grid: pull each h3 + the paragraph that follows
           it into a card-shaped box. Done via :has() so we don't
           need to touch the markdown source. Falls back to stacked
           rows on browsers without :has() support. */
        @supports (selector(:has(*))) {
          .lists-marketing.is-landing h2:first-of-type + h3,
          .lists-marketing.is-landing h2:first-of-type ~ h3 + p + h3,
          .lists-marketing.is-landing h2:first-of-type ~ h3 + p + h3 + p + h3 {
            /* placeholder — actual grid wrapping done below */
          }
        }
        /* Card-style treatment for h3+p pairs after the first h2.
           Walter 2026-05-13 round 8: each section gets EQUAL top
           and bottom padding so the heading is vertically centred
           inside its h3 segment and the body is vertically centred
           inside its p segment. Earlier passes had asymmetric
           padding (more on the outside, less between) which left
           the heading hugging the top of its area. With equal
           padding both pieces of text sit centred in their own
           bordered band, giving a cleaner "two stacked centred
           sections" look. */
        .lists-marketing.is-landing h3 {
          padding: 18px 22px;
          background: var(--surface2);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          border-bottom: none;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        .lists-marketing.is-landing h3 + p {
          padding: 22px;
          background: var(--surface2);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 var(--radius) var(--radius);
          margin-bottom: 18px;
          color: var(--text);
          line-height: 1.6;
        }
        .lists-marketing.is-landing ol {
          counter-reset: how-it-works;
          list-style: none;
          margin: 0 0 18px;
          padding: 0;
        }
        .lists-marketing.is-landing ol li {
          counter-increment: how-it-works;
          position: relative;
          padding: 14px 18px 14px 56px;
          margin-bottom: 10px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius);
        }
        .lists-marketing.is-landing ol li::before {
          content: counter(how-it-works);
          position: absolute;
          left: 18px; top: 14px;
          width: 26px; height: 26px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg);
          font-family: var(--mono);
          font-weight: 700;
          font-size: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
}

function footerLinkStyle(active) {
  return {
    color: active ? 'var(--accent)' : 'var(--text-dim)',
    textDecoration: 'none',
    fontWeight: active ? 600 : 400,
  };
}
